import { type FormEvent, useId, useState } from "react";
import { CheckCircle2, ChevronDownIcon, KeyRoundIcon, PlusIcon, ReceiptTextIcon, UserRoundIcon } from "lucide-react";

import { Button } from "@/lib/shadcn/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/lib/shadcn/ui/collapsible";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/lib/shadcn/ui/dialog";
import { Label } from "@/lib/shadcn/ui/label";
import { Separator } from "@/lib/shadcn/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/lib/shadcn/ui/table";
import type { NeteraAccount, StrOwnerLabel } from "@/models/responses";
import {
    useChangeNeteraAccountPasswordMutation,
    useChangeStrOwnerPasswordMutation,
    useGetNeteraAccountsQuery,
} from "@/store/filing-credentials-api";
import { useGetStrOwnersQuery } from "@/store/slices/apartments";

import { AddNeteraAccountDialog } from "./filing-credentials.add-account";
import { failureFor } from "./filing-credentials.errors";
import { CredentialRow, CredentialSection, FieldError, Note, SecretInput, SectionMessage } from "./filing-credentials.parts";

interface Holders {
    netera: NeteraAccount;
    taxis: StrOwnerLabel;
}

type CredentialSystem = keyof Holders;

type CredentialOf<S extends CredentialSystem> = { system: S; holder: Holders[S] };

type Credential = { [S in CredentialSystem]: CredentialOf<S> }[CredentialSystem];

interface SystemDescriptor<S extends CredentialSystem> {
    name: string;
    holderLabel: string;
    holderName: (holder: Holders[S]) => string;
    holderDetail: (holder: Holders[S]) => string | null;
    failures: Record<number, string>;
    info: string;
}

const NOTHING_SAVED = "nothing was saved";

const systems: { [S in CredentialSystem]: SystemDescriptor<S> } = {
    netera: {
        name: "Netera",
        holderLabel: "Account",
        holderName: (account) => account.username,
        holderDetail: () => null,
        failures: {
            404: `This Netera account no longer exists — ${NOTHING_SAVED}`,
            422: `Netera rejected this password — ${NOTHING_SAVED}`,
            423: `The Netera account is locked for a few minutes — try again later; ${NOTHING_SAVED}`,
            502: `Could not reach Netera — ${NOTHING_SAVED}`,
        },
        info: "We check the new password with Netera before saving. If Netera rejects it, nothing changes.",
    },
    taxis: {
        name: "Taxis",
        holderLabel: "Owner",
        holderName: (owner) => owner.name,
        holderDetail: (owner) => owner.taxisUsername,
        failures: {
            404: `STR has no such owner — ${NOTHING_SAVED}`,
            502: `STR did not accept the password change — ${NOTHING_SAVED}`,
        },
        info: "Use the password you set on Taxisnet. It is saved for STR uploads.",
    },
};

interface CredentialView {
    system: CredentialSystem;
    id: number;
    name: string;
    holderLabel: string;
    holder: string;
    holderDetail: string | null;
    failures: Record<number, string>;
    info: string;
}

function viewOf<S extends CredentialSystem>(credential: CredentialOf<S>): CredentialView {
    const { holderName, holderDetail, ...descriptor } = systems[credential.system];
    return {
        ...descriptor,
        system: credential.system,
        id: credential.holder.id,
        holder: holderName(credential.holder),
        holderDetail: holderDetail(credential.holder),
    };
}

function formatDay(value: Date | string): string {
    return new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function filingTargetCount(count: number): string {
    return `${count} filing ${count === 1 ? "target" : "targets"}`;
}

function ChangePasswordDialog({
    credential,
    onClose,
    onChanged,
}: {
    credential: Credential;
    onClose: () => void;
    onChanged: (credential: CredentialView) => void;
}) {
    const ids = useId();
    const [password, setPassword] = useState("");
    const [failure, setFailure] = useState<string | null>(null);
    const [changeNetera, neteraChange] = useChangeNeteraAccountPasswordMutation();
    const [changeTaxis, taxisChange] = useChangeStrOwnerPasswordMutation();
    const changePassword: Record<CredentialSystem, typeof changeNetera> = { netera: changeNetera, taxis: changeTaxis };
    const isSaving = neteraChange.isLoading || taxisChange.isLoading;
    const errorId = `${ids}-error`;
    const view = viewOf(credential);

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setFailure(null);
        const result = await changePassword[view.system]({ id: view.id, password });
        if (result.error) {
            setFailure(failureFor(result.error, view.failures, "The password was not changed. Try again."));
            return;
        }
        onChanged(view);
    };

    return (
        <Dialog open onOpenChange={(open) => !open && !isSaving && onClose()}>
            <DialogContent data-testid="change-password-dialog" className="sm:max-w-[440px]">
                <DialogHeader>
                    <DialogTitle data-testid="change-password-title">Change {view.name} password</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    <dl className="space-y-1.5">
                        <dt className="text-sm font-medium">{view.holderLabel}</dt>
                        <dd className="flex h-9 items-center gap-2 rounded-md border bg-muted/50 px-3 text-sm">
                            <span data-testid="change-password-holder" className="truncate font-medium">
                                {view.holder}
                            </span>
                            {view.holderDetail && <span className="truncate text-muted-foreground">{view.holderDetail}</span>}
                        </dd>
                    </dl>
                    <div className="space-y-1.5">
                        <Label htmlFor={`${ids}-password`}>New password</Label>
                        <SecretInput
                            id={`${ids}-password`}
                            testId="change-password-input"
                            secret="password"
                            autoComplete="new-password"
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            aria-invalid={failure ? "true" : undefined}
                            aria-describedby={failure ? errorId : undefined}
                            autoFocus
                        />
                        {failure && (
                            <FieldError id={errorId} testId="change-password-error">
                                {failure}
                            </FieldError>
                        )}
                    </div>
                    <Note>
                        <DialogDescription data-testid="change-password-info">{view.info}</DialogDescription>
                    </Note>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button data-testid="change-password-submit" type="submit" disabled={password === "" || isSaving}>
                            {isSaving ? "Saving…" : "Save"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function ChangePasswordButton({ testId, holder, onClick }: { testId: string; holder: string; onClick: () => void }) {
    return (
        <Button data-testid={testId} variant="outline" size="sm" onClick={onClick}>
            <KeyRoundIcon className="h-4 w-4" aria-hidden="true" />
            Change password <span className="sr-only">for {holder}</span>
        </Button>
    );
}

function NeteraAccountDescription({ account }: { account: NeteraAccount }) {
    return (
        <>
            <span className={account.apiKeySet ? undefined : "font-medium text-destructive"}>
                {account.apiKeySet ? "API key set" : "No API key"}
            </span>
            {` · Password changed ${formatDay(account.passwordChangedAt)} · ${filingTargetCount(account.targets.length)}`}
        </>
    );
}

function NeteraAccountRow({ account, onChangePassword }: { account: NeteraAccount; onChangePassword: () => void }) {
    const [targetsOpen, setTargetsOpen] = useState(false);

    return (
        <Collapsible open={targetsOpen} onOpenChange={setTargetsOpen} asChild>
            <li>
                <CredentialRow
                    data-testid={`netera-account-${account.id}`}
                    icon={ReceiptTextIcon}
                    title={account.username}
                    description={<NeteraAccountDescription account={account} />}
                    actions={
                        <>
                            <CollapsibleTrigger asChild>
                                <Button
                                    data-testid="filing-targets-toggle"
                                    variant="ghost"
                                    size="sm"
                                    className="text-muted-foreground"
                                >
                                    Filing targets <span className="sr-only">for {account.username}</span>
                                    <ChevronDownIcon
                                        className={`h-4 w-4 transition-transform motion-reduce:transition-none ${targetsOpen ? "rotate-180" : ""}`}
                                        aria-hidden="true"
                                    />
                                </Button>
                            </CollapsibleTrigger>
                            <ChangePasswordButton
                                testId={`netera-account-${account.id}-change-password`}
                                holder={account.username}
                                onClick={onChangePassword}
                            />
                        </>
                    }
                >
                    <CollapsibleContent className="border-t bg-muted/30 px-4 py-3 sm:pl-[4.25rem]">
                        <Table aria-label={`Filing targets for ${account.username}`}>
                            <TableHeader>
                                <TableRow className="hover:bg-transparent">
                                    <TableHead className="h-8">Target</TableHead>
                                    <TableHead className="h-8">Hotel ID</TableHead>
                                    <TableHead className="h-8">Branch ID</TableHead>
                                    <TableHead className="h-8 text-right">Active apartments</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {account.targets.map((target) => (
                                    <TableRow key={target.id} data-testid={`netera-target-${target.id}`} className="hover:bg-transparent">
                                        <TableCell className="py-1.5">{target.label}</TableCell>
                                        <TableCell className="py-1.5 tabular-nums">{target.hotelId}</TableCell>
                                        <TableCell className="py-1.5 tabular-nums">{target.branchId}</TableCell>
                                        <TableCell className="py-1.5 text-right tabular-nums">{target.apartmentCount}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CollapsibleContent>
                </CredentialRow>
            </li>
        </Collapsible>
    );
}

function LoadFailure({ what, onRetry }: { what: string; onRetry: () => void }) {
    return (
        <div className="flex flex-col items-center gap-3 pb-6">
            <SectionMessage role="alert">The {what} could not be loaded.</SectionMessage>
            <Button size="sm" variant="outline" onClick={onRetry}>
                Retry
            </Button>
        </div>
    );
}

function NeteraAccountsSection({
    onChangePassword,
    onAddAccount,
}: {
    onChangePassword: (credential: Credential) => void;
    onAddAccount: () => void;
}) {
    const { data: accounts = [], isLoading, isError, refetch } = useGetNeteraAccountsQuery();

    return (
        <CredentialSection
            testId="netera-accounts"
            title="Netera / MyData"
            description="Cloudhotel logins that file invoices to MyData, and the hotel and branch each one files for."
            action={
                <Button data-testid="add-netera-account" size="sm" onClick={onAddAccount} className="self-start sm:self-auto">
                    <PlusIcon className="h-4 w-4" aria-hidden="true" />
                    Add Netera account
                </Button>
            }
        >
            {isLoading ? (
                <SectionMessage>Loading Netera accounts…</SectionMessage>
            ) : isError ? (
                <LoadFailure what="Netera accounts" onRetry={() => void refetch()} />
            ) : accounts.length === 0 ? (
                <SectionMessage>No Netera accounts yet. Add one to start filing to MyData.</SectionMessage>
            ) : (
                <ul className="divide-y">
                    {accounts.map((account) => (
                        <NeteraAccountRow
                            key={account.id}
                            account={account}
                            onChangePassword={() => onChangePassword({ system: "netera", holder: account })}
                        />
                    ))}
                </ul>
            )}
        </CredentialSection>
    );
}

function StrOwnersSection({ onChangePassword }: { onChangePassword: (credential: Credential) => void }) {
    const { data, isLoading, isError, refetch } = useGetStrOwnersQuery();
    const owners = data?.owners ?? [];

    return (
        <CredentialSection
            testId="str-owners"
            title="STR / Taxis"
            description="Taxisnet logins that declare stays to the Short-Term Registry."
        >
            {isLoading ? (
                <SectionMessage>Loading STR owners…</SectionMessage>
            ) : isError ? (
                <LoadFailure what="STR owners" onRetry={() => void refetch()} />
            ) : owners.length === 0 ? (
                <SectionMessage>No STR owners yet.</SectionMessage>
            ) : (
                <ul className="divide-y">
                    {owners.map((owner) => (
                        <li key={owner.id}>
                            <CredentialRow
                                data-testid={`str-owner-${owner.id}`}
                                icon={UserRoundIcon}
                                title={owner.name}
                                description={owner.taxisUsername}
                                actions={
                                    <ChangePasswordButton
                                        testId={`str-owner-${owner.id}-change-password`}
                                        holder={owner.name}
                                        onClick={() => onChangePassword({ system: "taxis", holder: owner })}
                                    />
                                }
                            />
                        </li>
                    ))}
                </ul>
            )}
        </CredentialSection>
    );
}

export default function FilingCredentialsPage() {
    const [editing, setEditing] = useState<Credential | null>(null);
    const [adding, setAdding] = useState(false);
    const [confirmation, setConfirmation] = useState<string | null>(null);

    const startEditing = (credential: Credential) => {
        setConfirmation(null);
        setEditing(credential);
    };

    const startAdding = () => {
        setConfirmation(null);
        setAdding(true);
    };

    const finishEditing = (credential: CredentialView) => {
        setEditing(null);
        setConfirmation(`${credential.name} password changed for ${credential.holder}.`);
    };

    const finishAdding = (username: string) => {
        setAdding(false);
        setConfirmation(`Netera account ${username} added.`);
    };

    return (
        <div data-testid="filing-credentials-page" className="space-y-8">
            <div className="space-y-1">
                <h1 className="text-2xl font-semibold tracking-tight">Filing credentials</h1>
                <p className="text-muted-foreground">
                    The logins Keros files with. Passwords and API keys are never shown here — you can only replace them.
                </p>
            </div>

            <Separator />

            <div data-testid="filing-credentials-confirmation" role="status" className="empty:mb-0">
                {confirmation && (
                    <p className="flex items-center gap-2 rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800">
                        <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
                        {confirmation}
                    </p>
                )}
            </div>

            <NeteraAccountsSection onChangePassword={startEditing} onAddAccount={startAdding} />
            <StrOwnersSection onChangePassword={startEditing} />

            {editing && <ChangePasswordDialog credential={editing} onClose={() => setEditing(null)} onChanged={finishEditing} />}
            {adding && <AddNeteraAccountDialog onClose={() => setAdding(false)} onAdded={finishAdding} />}
        </div>
    );
}
